import { ENDPOINTS, Status, StorageKeys } from '@constants';
import { AppStorage } from '@core';
import { BaseResponse, IAdvanceSearch } from '@dto';
import { IUseAgInfTableProps, IUseAgInfTableResult } from '@hooks-dto';
import APIManager from '@services';
import { exportDataToExcel } from '@utils/excel';
import {
  CellValueChangedEvent,
  RowDataUpdatedEvent,
  SelectionChangedEvent,
  DragStoppedEvent,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  GetRowIdParams
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { cloneDeep, compact, get, isEmpty, set, throttle } from 'lodash';
import qs from 'query-string';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useCancelablePromise } from 'uikit-common';

interface IAgGridStorageConfigs {
  columnStates?: Record<string, any>;
}

const useAgInfTable = <T = any>({
  name,
  tableName,
  colDefs,

  mappedFields,
  otherParams,
  apiURL,
  dataPath = 'data.data',
  onGetRowId,
  onTransformData,

  onAdd,
  onAddParams,

  onEndEditing,

  onExportParams,

  showFilter,

  pageSize = 50,

  isAddExtraRow = false,
  isInitFetch = true
}: IUseAgInfTableProps<T>): IUseAgInfTableResult<T> => {
  const gridRef = useRef<AgGridReact<T>>(null);

  const [selectedData, setSelectedData] = useState<T[]>([]);

  const [keyword, setKeyword] = useState<string>('');
  const [searchField] = useState('keyword');

  const [isLoading, setIsLoading] = useState(false);

  const [advanceSearch, setAdvanceSearch] = useState<IAdvanceSearch[]>([]);
  const [searchConditions, setSearchConditions] = useState<any[]>([]);

  const { promise, handleNewPromise } = useCancelablePromise();

  const cacheBlockSize = pageSize;

  const mParams = useMemo(() => {
    const _mParams: any = {
      size: cacheBlockSize,
      ...otherParams
    };

    if (!isEmpty(keyword)) {
      const _searchField = mappedFields?.[searchField] || searchField;
      _mParams[_searchField] = keyword;
    }

    if (!isEmpty(searchConditions)) {
      _mParams.searchCondition = JSON.stringify(searchConditions);
    }

    return _mParams;
  }, [
    cacheBlockSize,
    keyword,
    mappedFields,
    otherParams,
    searchConditions,
    searchField
  ]);

  const onFetchFilter = useCallback(async () => {
    if (showFilter && tableName) {
      const res = await APIManager.request({
        url: ENDPOINTS.advanceSearch(),
        body: {
          tableName,
          status: Status.Active
        }
      });

      if (!res.error && res.data) {
        setAdvanceSearch(
          (res.data as IAdvanceSearch[])
            .reduce((acc, cur) => {
              const filterField = (colDefs ?? []).find(c => {
                return (
                  [
                    c.field,
                    c.colId,
                    c.mappedField,
                    mappedFields?.[c?.colId ?? c?.field] || c?.colId
                  ].includes(cur.columnName) &&
                  (c.sortable ?? true)
                );
              });

              if (filterField) {
                acc.push({
                  ...cur,
                  label: filterField?.headerName
                });
              }

              return compact(acc);
            }, [] as IAdvanceSearch[])
            .sort((a: IAdvanceSearch, b: IAdvanceSearch) => {
              if (!a?.label || !b.label) {
                return 1;
              }
              return a.label?.localeCompare(b?.label ?? '');
            })
        );
      }
    }
  }, [colDefs, mappedFields, showFilter, tableName]);

  const dataSource: IDatasource = useMemo(() => {
    return {
      rowCount: undefined, // behave as infinite scroll

      getRows: throttle(async (params: IGetRowsParams) => {
        const { startRow, sortModel, successCallback } = params;
        if (!isInitFetch) {
          setTimeout(() => {
            successCallback([], 0);
          }, 0);
        } else {
          if (apiURL) {
            onFetchFilter();
            const _mParams: any = cloneDeep(mParams);

            const page = startRow / cacheBlockSize;
            _mParams['page'] = page;

            if (sortModel && sortModel.length > 0) {
              const { colId, sort } = sortModel?.[0] || {};
              const _sortField = mappedFields?.[colId] || colId;
              _mParams['sortBy'] = `${_sortField}:${sort}`;
            }

            const _apiURL = `${apiURL}?${qs.stringify(_mParams)}`;

            setIsLoading(true);
            handleNewPromise(
              APIManager.request({
                url: _apiURL
              })
            );

            try {
              const res: BaseResponse<any> = await promise.current;
              const resData = get(res, dataPath);
              if (resData) {
                const data = resData ?? [];
                const _data = onTransformData?.(data) ?? data;

                let lastRow = -1;
                if (isAddExtraRow ? _data.length : res?.data?.total) {
                  lastRow = isAddExtraRow ? _data.length : res?.data?.total;
                } else {
                  if (data.length < pageSize) {
                    lastRow = startRow + data.length;
                  }
                }

                setTimeout(() => {
                  successCallback(_data, lastRow);
                }, 0);
              }
            } catch (error) {
              console.log('Error fetching data', error);
            } finally {
              setTimeout(() => {
                setIsLoading(false);
              }, 0);
            }
          }
        }
      }, 200)
    };
  }, [
    isInitFetch,
    apiURL,
    onFetchFilter,
    mParams,
    cacheBlockSize,
    handleNewPromise,
    mappedFields,
    promise,
    dataPath,
    onTransformData,
    isAddExtraRow,
    pageSize
  ]);

  const onGetData = useCallback(() => {
    const rows: T[] = [];
    gridRef.current?.api?.forEachNode((rowNode: any) =>
      rows.push(rowNode.data as T)
    );
    return rows;
  }, []);

  const onGetSelectedData = useCallback(() => {
    return gridRef.current?.api?.getSelectedRows() ?? [];
  }, []);

  const onSelectionChanged = useCallback((event: SelectionChangedEvent<T>) => {
    setSelectedData(event.api.getSelectedRows());
  }, []);

  const onCellValueChanged = useCallback(
    (props: CellValueChangedEvent<T>) => {
      const { api } = props;
      const rows: T[] = [];
      api.forEachNode(rowNode => rows.push(rowNode.data as T));
      onEndEditing?.(rows);
    },
    [onEndEditing]
  );

  const onDragStopped = useCallback(
    ({ api }: DragStoppedEvent<any>) => {
      const agGridConfigs = (AppStorage.get(StorageKeys.agGrid) ??
        {}) as IAgGridStorageConfigs;
      const _configs = cloneDeep(agGridConfigs);
      set(_configs, `columnStates.${name}`, api.getColumnState());
      AppStorage.set(StorageKeys.agGrid, _configs);
    },
    [name]
  );

  const onGridReady = useCallback(
    ({ api }: GridReadyEvent<any>) => {
      // onFetchFilter();
      const agGridConfigs = (AppStorage.get(StorageKeys.agGrid) ??
        {}) as IAgGridStorageConfigs;
      const _columnState = agGridConfigs?.columnStates?.[name];
      if (_columnState) {
        api.applyColumnState({ state: _columnState, applyOrder: true });
      }
    },
    [name]
  );

  const onRowDataUpdated = useCallback(
    ({ api }: RowDataUpdatedEvent<T>) => {
      const agGridConfigs = (AppStorage.get(StorageKeys.agGrid) ??
        {}) as IAgGridStorageConfigs;
      const _columnState = agGridConfigs?.columnStates?.[name];
      if (_columnState) {
        api.applyColumnState({ state: _columnState, applyOrder: true });
      }
    },
    [name]
  );

  const getRowId = useCallback(
    (params: GetRowIdParams) => {
      return onGetRowId ? onGetRowId(params) : params?.data?.id.toString();
    },
    [onGetRowId]
  );

  const onRefresh = useCallback(() => {
    setTimeout(() => {
      gridRef.current?.api?.setGridOption('datasource', dataSource);
    }, 100);
  }, [dataSource]);

  const onExport = useCallback(
    async (fileName?: string) => {
      if (gridRef.current) {
        const exportData =
          gridRef.current.api
            .getDataAsCsv({
              suppressQuotes: true,
              columnSeparator: '__'
            })
            ?.split('\r\n')
            .map((i: any) => {
              return i.split('__');
            }) ?? [];

        const columnWidths = gridRef.current.api
          .getColumnState()
          .map((i: any) => i.width) as number[];

        const exportParams = {
          ...onExportParams,
          columnWidths
        };

        await exportDataToExcel(
          fileName || 'report.xlsx',
          exportData,
          exportParams
        );
      }
    },
    [onExportParams]
  );

  return {
    gridRef,

    name,

    columnDefs: colDefs,
    selectedData,
    keyword,
    onAddParams,
    rowModelType: 'infinite',
    // datasource: !isInitFetch ? undefined : dataSource,
    datasource: dataSource,
    isLoading,
    cacheBlockSize,
    pagination: false,
    advanceSearch,
    searchConditions,
    showFilter,

    setSearchConditions,

    onAdd,
    onExport,
    onSearch: setKeyword,
    onGetData,
    onGetSelectedData,
    onRefresh,

    onSelectionChanged,
    onRowDataUpdated,
    onCellValueChanged,
    onDragStopped,
    onGridReady,
    getRowId
  };
};

export default useAgInfTable;
